import React, { useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // Added for visual aid
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";

const UploadDaysPreviewModal = ({ isOpen, onClose, onSubmit, previewData, onFileSelect, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // --- Drag and Drop Handlers ---
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.name.endsWith('.xlsx'))) {
      onFileSelect(file);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) onFileSelect(file);
  };

  const customRender = (row, accessor) => {
    if (accessor === "timeIn" || accessor === "timeOut")
      return row[accessor] ? new Date(row[accessor]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-";
    if (accessor === "errors" && row[accessor])
      return <span className="text-red-600 font-bold">{row[accessor]}</span>;
    return row[accessor] || "-";
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[90%] lg:w-[75%] rounded-lg shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-blue-900 tracking-tight">Excel Upload Preview</h2>
            <p className="text-xs text-gray-500 uppercase font-semibold">Review generated logs before final submission</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow">

          {/* STEP 1: DRAG & DROP FILE SELECTION */}
          <div className="mb-6 flex flex-col gap-2">
            <span className="text-sm font-bold text-blue-800 uppercase">Step 1: Select Excel File (.xlsx)</span>

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
              className={`
                relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-all cursor-pointer
                ${isDragging ? "border-blue-500 bg-blue-100" : "border-blue-200 bg-blue-50 hover:bg-blue-100/50"}
                ${loading ? "opacity-50 pointer-events-none" : ""}
              `}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx"
                onChange={handleFileChange}
                className="hidden"
              />

              <CloudUploadIcon className={`${isDragging ? "text-blue-600" : "text-blue-400"} mb-2`} sx={{ fontSize: 48 }} />

              <div className="text-center">
                <p className="text-sm font-bold text-blue-900">
                  {loading ? "Processing File..." : "Click to browse or drag and drop your Excel file here"}
                </p>
                <p className="text-xs text-blue-500 font-medium mt-1">Only .xlsx files are supported</p>
              </div>

              {loading && (
                <div className="absolute inset-0 bg-white/20 flex items-center justify-center rounded-xl">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>

          {/* STEP 2: PREVIEW TABLE */}
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-600 uppercase px-1">Step 2: Preview Generated Entries</span>
            {previewData.length > 0 && (
              <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">
                {previewData.length} Logs Detected
              </span>
            )}
          </div>

          <div className="border rounded-lg overflow-hidden shadow-sm">
            <CreateTable
              tableName="UploadPreviewTable"
              headers={["Date", "Emp #", "Name", "In", "Out", "Status"]}
              accessors={["date", "employeeNumber", "fullName", "timeIn", "timeOut", "errors"]}
              data={previewData}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={5}
              totalItems={previewData.length}
              customCellRenderer={customRender}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-bold border-none cursor-pointer transition-colors">
            Cancel
          </button>
          <button
            disabled={loading || previewData.length === 0 || previewData.some(i => i.errors)}
            onClick={() => onSubmit(previewData)}
            className="bg-green-600 hover:bg-green-700 text-white px-10 py-2 rounded-lg font-bold border-none cursor-pointer shadow-md disabled:opacity-50 transition-all"
          >
            {loading ? "Processing..." : `Confirm & Upload ${previewData.length} Records`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDaysPreviewModal;